import isObject from "lodash-es/isObject";
import { type NextRouter } from "next/router";
import { type MouseEvent as ReactMouseEvent } from "react";

import { makeUrlPrefix } from "@moment/api-collab/api-types/index";

export type MessageType =
	| "getAuth0User"
	| "getAccessTokenSilently"
	| "getIdTokenClaims"
	| "iframeReady"
	| "isAuthenticated"
	| "loginWithRedirect"
	| "logoutWithRedirect"
	| "ping"
	| "routerPathChange"
	| "setTheme";

export interface MessageDataArgs {
	type: MessageType;
	args?: unknown;
}

export type IFrameDocumentPathData = {
	workspaceName: string;
	documentId: string;
	pageId: string | undefined;
};

export const generateIframeUrlPath = (
	workspaceName: string,
	documentId: string,
	pageSlugParam: string
) => {
	if (pageSlugParam === undefined || pageSlugParam === "") {
		return `/d/${workspaceName}/${documentId}`;
	}
	return `/d/${workspaceName}/${documentId}/${pageSlugParam}`;
};

export const parseIframeUrlPath = (url: string) => {
	const urlObj = new URL(url);
	const pathParts = urlObj.pathname
		.split("/")
		.filter(Boolean)
		.filter((part) => part !== "d");
	if (pathParts.length < 2) {
		throw new Error("Invalid iframe URL path");
	}

	const workspaceName = pathParts[0];
	const documentId = pathParts[1];
	const pageId = pathParts.length > 2 ? pathParts[2] : undefined;
	return {
		workspaceName,
		documentId,
		pageId,
	} as IFrameDocumentPathData;
};

/**
 * Returns `true` if the current window's parent is not
 * itself. In other words, the current window is running
 * inside an iframe if this function returns `true`.
 *
 * **NOTE**: This function must be called inside hooks
 * to ensure it runs only on the client-side.
 */
export const isRunningInIframe = () => {
	if (typeof window === "undefined") {
		return false;
	}

	return window.parent !== window.self;
};

export const sendPostMessage = (messageType: MessageType, args?: unknown, origin?: string) => {
	if (!isRunningInIframe()) {
		throw new Error("This function cannot be called in the parent frame");
	}

	// I am leaving this in as it was pretty helpful to debug issues with iframe message passing.
	// console.log("sendPostMessage called with the following", {
	// 	messageType,
	// 	args,
	// 	origin,
	// });

	// The "*" for target origin is safe here since we use a CSP
	// to enfore frame-ancestors.
	window.parent.postMessage({ type: messageType, args }, origin ?? "*");
};

export async function invokePostMessage<T>(
	messageType: MessageType,
	args?: unknown,
	origin?: string
): Promise<T | undefined> {
	return await new Promise<T | undefined>((resolve, reject) => {
		if (!isRunningInIframe()) {
			reject(new Error("This function cannot be called in the parent frame"));
			return;
		}

		const channel = new MessageChannel();
		channel.port1.start();
		channel.port1.addEventListener("message", (event: MessageEvent) => {
			channel.port1.close();

			if (event.data?.err) {
				reject(event.data.err);
				return;
			}
			if (event.data) {
				resolve(event.data as T);
				return;
			}

			resolve(undefined);
		});
		// The "*" for target origin is safe here since we use a CSP
		// to enfore frame-ancestors.
		window.parent.postMessage({ type: messageType, args }, origin ?? "*", [channel.port2]);
	});
}

interface RouteProps {
	workspaceName: string;
	documentId: string;
	pageSlug: string;
}

export async function handleIframeNavigation(
	e: MouseEvent | ReactMouseEvent<HTMLAnchorElement, MouseEvent>,
	route: RouteProps | string,
	router?: NextRouter
) {
	if (!isRunningInIframe()) {
		return;
	}

	// The href for the anchor tag when running inside an iframe
	// has the parent frame's URL, so we don't want to let the
	// default click behavior to go through.
	e.preventDefault();
	e.stopPropagation();

	// Instead, perform a client-side routing manually.
	const url = isObject(route)
		? `${makeUrlPrefix(route.workspaceName, route.documentId, undefined)}/${route.pageSlug}`
		: route;

	// If we're in the Next.js router, use it to navigate
	if (router) {
		await router.push(url);
	} else {
		// Otherwise, use the browser's history to navigate
		window.location.href = url;
	}
}
